import React, { useEffect, useState, useCallback } from "react";
import { Heart, RefreshCw, TrendingUp, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export const AdminWishlistStats = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/wishlist/stats", { headers });
            setStats(data);
        } catch {
            toast.error("Failed to load stats");
        }
        setLoading(false);
        // eslint-disable-next-line
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="admin-wishlist-stats">
            <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-xl font-bold text-white inline-flex items-center gap-2">
                    <Heart size={18} className="text-[var(--gold)]" /> Wishlist Insights
                </h3>
                <button onClick={load} className="btn-neon" data-testid="admin-wishlist-refresh">
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {/* Summary cards */}
            {stats && (
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    <div className="glass-elevated rounded-2xl p-5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)]">Total saves across users</div>
                        <div className="font-display text-3xl font-extrabold text-white mt-1" data-testid="wishlist-total-items">{stats.total_items}</div>
                    </div>
                    <div className="glass-elevated rounded-2xl p-5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)]">Users with a wishlist</div>
                        <div className="font-display text-3xl font-extrabold text-white mt-1" data-testid="wishlist-total-users">{stats.total_users_with_wishlist}</div>
                    </div>
                </div>
            )}

            <h4 className="font-display text-base font-bold text-white mb-3 inline-flex items-center gap-2">
                <TrendingUp size={14} className="text-[var(--gold)]" /> Most wishlisted
            </h4>

            {loading ? (
                <div className="text-center py-12 text-[var(--text-secondary)]">Loading…</div>
            ) : !stats || stats.top_products.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center text-[var(--text-secondary)]" data-testid="admin-wishlist-empty">
                    No wishlist activity yet.
                </div>
            ) : (
                <div className="space-y-2">
                    {stats.top_products.map((p, idx) => (
                        <div
                            key={p.slug}
                            className="glass-elevated rounded-xl p-3 flex items-center gap-3"
                            data-testid={`admin-wishlist-top-${p.slug}`}
                        >
                            <div className="font-mono text-[var(--gold)] font-bold w-6 text-right">{idx + 1}</div>
                            {p.image && <img src={p.image} alt={p.name} className="w-16 h-11 object-contain rounded-lg" />}
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-white flex items-center gap-2">
                                    {p.name}
                                    {p.on_sale && (
                                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider border border-[var(--gold)]/40 text-[var(--gold)] inline-flex items-center gap-0.5">
                                            <Tag size={9} /> Sale
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-[var(--text-secondary)]">{p.slug} · A${Number(p.effective_price || 0).toFixed(2)}</div>
                            </div>
                            <div className="text-right">
                                <div className="font-display text-xl font-bold text-white">{p.count}</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">saves</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
